import { useState, type FC } from "react";
import { SendIcon } from "../../atoms/icons/SendIcon";
import { theme } from "../../../theme";
import Modal from "../Modal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../Alert";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import IconButton from "../../atoms/buttons/IconButton";
import BodySmall from "../../atoms/typography/BodySmall";
import { useSendForInterpretationWithoutTemplate } from "../../../graphql/hooks/documentHandler/useSendForInterpretationWithoutTemplate";
import { useTranslation } from "react-i18next";

interface Props {
  documentId: string;
  uploadUnitId: string;
}

const InterpretWithoutTemplateButton: FC<Props> = ({
  documentId,
  uploadUnitId,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCompletedInterpretationWithoutTemplate = (result: any) => {
    if (
      result?.documentHandler?.sendForInterpretationWithoutTemplate?.success ===
      true
    ) {
      setErrorMessage("");
      setSuccessMessage(
        t("documentHandler.interpretWithoutTemplateSuccessful"),
      );
    } else {
      setSuccessMessage("");
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorInterpretationWithoutTemplate = (res?: any) => {
    setErrorMessage(
      `${t("errors.somethingWentWrong")}, ${error?.toString()}, ${res?.toString()}`,
    );
  };
  const { interpretWithoutTemplate, loading, error } =
    useSendForInterpretationWithoutTemplate({
      onCompleted: onCompletedInterpretationWithoutTemplate,
      onError: onErrorInterpretationWithoutTemplate,
    });
  const onClickInterpretationWithoutTemplate = async () => {
    if (!uploadUnitId || !documentId) {
      setErrorMessage(t("documentHandler.uploadUnitOrDocumentMissing"));
      return;
    }

    try {
      await interpretWithoutTemplate({
        variables: {
          input: {
            uploadDocumentId: documentId,
            uploadUnitId: uploadUnitId,
          },
        },
      });
    } catch (e) {
      setErrorMessage(
        `${t("documentHandler.couldNotProcessInterpret")}: ${e?.toString()}`,
      );
    }
  };

  return (
    <div>
      <Tooltip
        content={
          <>
            <Body color={theme.colors.white}>
              {t("documentHandler.interpretWithoutTemplate")}
            </Body>
            <BodySmall color="white">
              {t("documentHandler.interpretWithoutTemplateInfo1")}
            </BodySmall>
            <BodySmall color="white">
              {t("documentHandler.interpretWithoutTemplateInfo2")}
            </BodySmall>
          </>
        }
        width="400px"
      >
        <IconButton
          icon={<SendIcon />}
          onClick={(e?: React.MouseEvent) => {
            e?.stopPropagation();
            setModalOpen(true);
          }}
          size="medium"
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={600}
          title={t("documentHandler.interpretWithoutTemplate")}
        >
          <Body>{t("documentHandler.interpretWithoutTemplateInfo1")}</Body>
          <HorizontalSpacer spacing={1} />
          <Body>{t("documentHandler.interpretWithoutTemplateInfo2")}</Body>
          <HorizontalSpacer spacing={2} />
          {errorMessage && (
            <Alert
              type="error"
              title={errorMessage}
              message={error?.toString()}
            />
          )}
          {successMessage && <Alert type="success" title={successMessage} />}

          {!successMessage && (
            <div>
              <HorizontalSpacer spacing={1} />
              <ActionButton
                title={t("documentHandler.interpretWithoutTemplate")}
                icon={<SendIcon color={theme.colors.white} />}
                color={theme.colors.primary}
                loading={loading}
                onClick={onClickInterpretationWithoutTemplate}
                disabled={
                  successMessage && successMessage.length < 1 ? true : false
                }
              />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default InterpretWithoutTemplateButton;
